import React from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO, { BreadcrumbSchema, ServiceSchema } from "../components/SEO";
import { Link } from "react-router-dom";
import { FadeUp } from "../components/Motion";

const points = [
  { t: "FEMA Public Assistance", d: "Project formulation, documentation and management from damage to closeout." },
  { t: "Damage assessments and scope development", d: "Field-driven scopes that hold up under federal review." },
  { t: "Cost estimating and cost reasonableness", d: "RSMeans and market-based estimates, plus independent review of contractor pricing." },
  { t: "Procurement and contract compliance", d: "2 CFR 200 procurement support and contract-file review." },
  { t: "Hazard mitigation", d: "Section 406 mitigation proposals, cost estimates and benefit-cost support." },
  { t: "Teaming and subcontract support", d: "A technical resource for engineering firms, recovery primes and program managers." },
];

const disasters = [
  { event: "Hurricane Debby", dr: "DR-4806-FL", year: "2024" },
  { event: "Hurricane Ian", dr: "DR-4673-FL", year: "2022" },
  { event: "Hurricane Fiona", dr: "DR-4671-PR", year: "2022" },
  { event: "Hurricane Ida", dr: "DR-4615-NY", year: "2021" },
  { event: "Puerto Rico Earthquakes", dr: "DR-4473-PR", year: "2020" },
  { event: "Hurricane María", dr: "DR-4339-PR", year: "2017" },
  { event: "Hurricane Irma", dr: "DR-4337-FL", year: "2017" },
  { event: "Hurricane Matthew", dr: "DR-4283-FL", year: "2016" },
  { event: "Hurricanes Charley, Ivan and Wilma", dr: "USACE response led by Bob Carpenter", year: "2004 to 2005" },
];

const stats = [
  { n: "150+", t: "FEMA Public Assistance grants managed in Puerto Rico and Florida, over $300M in estimated value" },
  { n: "60+", t: "Private nonprofit organizations served, including 100+ houses of worship" },
  { n: "$10M+", t: "Category A debris mission in Aguada, Puerto Rico, with 30+ staff" },
];

export default function DisasterRecovery() {
  return (
    <>
      <SEO
        title="FEMA Public Assistance Consulting"
        description="FEMA Public Assistance management, cost estimating, procurement compliance and hazard mitigation from a construction firm. 150+ grants managed."
        path="/disaster-recovery"
      />
      <BreadcrumbSchema items={[{ name: "Home", url: "https://bcpeabody.com/" }, { name: "Disaster Recovery", url: "https://bcpeabody.com/disaster-recovery" }]} />
      <ServiceSchema serviceType="FEMA Public Assistance consulting and disaster recovery" path="/disaster-recovery" />
      <Nav transparentOnTop />

      <section className="relative min-h-[55vh] flex items-end overflow-hidden bg-ink text-cream-light">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-copper/10 blur-[160px]" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 w-full pb-14 pt-32">
          <div className="text-[11px] tracking-[0.18em] uppercase text-copper-light mb-5">Disaster Recovery</div>
          <h1 className="font-serif font-medium tracking-tight mb-5 leading-[1.05]" style={{ fontSize: "clamp(32px, 5vw, 60px)" }}>
            Disaster Recovery & FEMA Public Assistance
          </h1>
          <p className="text-cream-light/80 max-w-3xl" style={{ fontSize: 18 }}>
            Recovery support from a firm that builds.
          </p>
        </div>
      </section>

      <section className="bg-cream-light py-16 md:py-20">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="text-ink/85 leading-relaxed" style={{ fontSize: 18 }}>
              We help public agencies, nonprofits and recovery firms develop eligible scopes, estimate and validate costs, stay compliant and move projects through closeout. Because we build, we understand how recovery projects are actually scoped, priced, procured and constructed.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {points.map((p, i) => (
            <FadeUp key={p.t} delay={i * 60}>
              <div className="border-t border-copper pt-5">
                <div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-3">0{i+1}</div>
                <h3 className="font-serif text-2xl text-ink mb-3 tracking-tight">{p.t}</h3>
                <p className="text-ink/75 leading-relaxed">{p.d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="dr-recovery-experience" className="bg-cream-light py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <h2 className="font-serif font-medium text-ink tracking-tight mb-10" style={{ fontSize: "clamp(28px, 3.6vw, 40px)" }}>
            Recovery experience
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
            {stats.map((s) => (
              <div key={s.n} className="border-l-2 border-copper pl-5">
                <div className="font-serif text-5xl md:text-6xl text-ink leading-none mb-3">{s.n}</div>
                <div className="text-sm text-ink/75 leading-snug">{s.t}</div>
              </div>
            ))}
          </div>

          <div data-testid="dr-disasters" className="mb-14">
            <h2 className="font-serif font-medium text-ink tracking-tight mb-4" style={{ fontSize: "clamp(28px, 3.6vw, 40px)" }}>
              Disasters our leadership has worked
            </h2>
            <p className="text-ink/75 max-w-2xl mb-10">
              Recovery experience from both sides of the program: as the Corps of Engineers, and as the team helping applicants recover.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10">
              {disasters.map((d) => (
                <div key={`${d.event}-${d.year}`} className="bg-cream p-6 border-l border-copper">
                  <p className="font-serif text-lg text-ink">{d.event}</p>
                  <p className="text-[11px] tracking-[0.14em] uppercase text-copper mt-2">{d.dr}</p>
                  <p className="text-[11px] tracking-[0.14em] uppercase text-ink/55 mt-1">{d.year}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-10 text-ink/85 leading-relaxed max-w-4xl" style={{ fontSize: 17 }}>
            <div>
              <h3 className="font-serif text-2xl text-ink tracking-tight mb-3">Puerto Rico (past clients)</h3>
              <p>
                After Hurricanes Irma and María, BCPeabody managed FEMA Public Assistance recovery across the island for two municipal governments, including the Municipality of Aguada, and for private nonprofit and faith-based organizations, including:
              </p>
              <p className="mt-3 text-ink/75">
                Baptist Churches of Puerto Rico · Iglesias Cristiana Discípulos de Cristo (Oficina Central), 110 churches · Concilio Fuente de Agua Viva · La Iglesia de Dios · Concilio de Iglesias de Cristo Misionera · Misión Internacional · Concilio Las Asambleas de Dios, Inc.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-2xl text-ink tracking-tight mb-3">Florida</h3>
              <p>
                Debris monitoring for the Village of Biscayne Park after Hurricane Irma, and FEMA Public Assistance support for Florida churches and nonprofits after Hurricanes Ian and Debby.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-2xl text-ink tracking-tight mb-3">Leadership experience</h3>
              <p>
                Col. (Ret.) Bob Carpenter led the Corps of Engineers' response to Hurricanes Charley, Ivan and Wilma, then served as president of a national disaster debris removal contractor from 2006 to 2011. Kimberly Carpenter managed FEMA Public Assistance recovery for multiple public agencies after Hurricane Ida from 2023 to 2026, including school construction, public safety, library and economic development agencies: 50+ projects and over $300M in estimated value.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ink text-cream-light py-20 text-center">
        <div className="max-w-[800px] mx-auto px-6">
          <h2 className="font-serif font-medium mb-8 tracking-tight" style={{ fontSize: "clamp(26px, 3.2vw, 36px)" }}>Talk with us about your program.</h2>
          <Link to="/contact?type=disaster-recovery" className="btn-copper">Start a Project →</Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
